import { apiClient } from '@/lib/api-client';
import {
  ProjetoT,
  ProjetoTDashboard,
  ProjetoStatus,
  ProjetoInvestimento,
  ProjetoTVinculos,
  CalendarioItemVirtual,
  ApiResponse,
} from '@/types';

// Seções, atividades, anexos, responsáveis e histórico de um ProjetoT são reaproveitados
// direto do módulo de Cronograma: um ProjetoT é um cronograma, então esses serviços já
// funcionam sobre o mesmo registro. Só o que é específico do projeto (workflow de aprovação,
// líder/tipo/objetivo, investimentos, dashboard agregado) vive em /projetot.
export {
  cronogramaSecaoService as projetotSecaoService,
  cronogramaAtividadeService as projetotAtividadeService,
  cronogramaAnexoService as projetotAnexoService,
  cronogramaResponsavelService as projetotResponsavelService,
  cronogramaSubtarefaService as projetotSubtarefaService,
} from './cronogramasApi';

type ProjetoTPayload = Partial<ProjetoT> & {
  investimentos?: Omit<ProjetoInvestimento, 'id' | 'projeto_id' | 'cronograma_id'>[];
};

export const projetotService = {
  async getAll(status?: ProjetoStatus, empresa_id?: string): Promise<ApiResponse<ProjetoT[]>> {
    try {
      const params = new URLSearchParams();
      if (status) params.set('status', status);
      if (empresa_id) params.set('empresa_id', empresa_id);
      const qs = params.toString();
      return await apiClient.get<ApiResponse<ProjetoT[]>>(`/projetot${qs ? `?${qs}` : ''}`);
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao buscar projetos', data: [] };
    }
  },

  async getById(id: string): Promise<ApiResponse<ProjetoT>> {
    try {
      return await apiClient.get<ApiResponse<ProjetoT>>(`/projetot/${id}`);
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao buscar projeto', data: {} as ProjetoT };
    }
  },

  async create(data: ProjetoTPayload): Promise<ApiResponse<ProjetoT>> {
    try {
      return await apiClient.post<ApiResponse<ProjetoT>>('/projetot', data);
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao criar projeto', data: {} as ProjetoT };
    }
  },

  async update(id: string, data: ProjetoTPayload): Promise<ApiResponse<ProjetoT>> {
    try {
      return await apiClient.put<ApiResponse<ProjetoT>>(`/projetot/${id}`, data);
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao atualizar projeto', data: {} as ProjetoT };
    }
  },

  async delete(id: string): Promise<ApiResponse<boolean>> {
    try {
      return await apiClient.delete<ApiResponse<boolean>>(`/projetot/${id}`);
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao excluir projeto', data: false };
    }
  },

  async updateStatus(id: string, status: ProjetoStatus): Promise<ApiResponse<ProjetoT>> {
    try {
      return await apiClient.patch<ApiResponse<ProjetoT>>(`/projetot/${id}/status`, { status });
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao atualizar status', data: {} as ProjetoT };
    }
  },

  async criarAPartir(modeloId: string, data: ProjetoTPayload): Promise<ApiResponse<ProjetoT>> {
    try {
      return await apiClient.post<ApiResponse<ProjetoT>>(`/projetot/${modeloId}/criar-a-partir`, data);
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao criar projeto a partir do modelo', data: {} as ProjetoT };
    }
  },

  async getVinculos(id: string): Promise<ApiResponse<ProjetoTVinculos>> {
    try {
      return await apiClient.get<ApiResponse<ProjetoTVinculos>>(`/projetot/${id}/vinculos`);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao buscar vínculos',
        data: { organogramas: [], fluxogramas: [] },
      };
    }
  },

  async getDashboard(empresa_id?: string): Promise<ApiResponse<ProjetoTDashboard>> {
    try {
      const url = empresa_id ? `/projetot/dashboard?empresa_id=${empresa_id}` : '/projetot/dashboard';
      return await apiClient.get<ApiResponse<ProjetoTDashboard>>(url);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao buscar dashboard',
        data: {
          totais: { total: 0, em_edicao: 0, ag_aprovacao: 0, em_andamento: 0, ag_validacao: 0, finalizado: 0, reprovado: 0 },
          por_tipo: [],
          por_mes: [],
          total_atividades: 0,
          progresso_medio: 0,
        },
      };
    }
  },

  // Itens virtuais (projetos e atividades com data) pra exibição no módulo Calendário — não são
  // eventos reais de `calendario_eventos`, nada é persistido; leitura direta e em tempo real.
  async getCalendarioEventos(params: { start?: string; end?: string; empresa_id?: string; escopo?: 'pessoa'; empresaIds?: string[] } = {}): Promise<ApiResponse<CalendarioItemVirtual[]>> {
    try {
      const query = new URLSearchParams();
      if (params.start) query.append('start', params.start);
      if (params.end) query.append('end', params.end);
      if (params.empresa_id) query.append('empresa_id', params.empresa_id);
      if (params.escopo) query.append('escopo', params.escopo);
      if (params.empresaIds?.length) query.append('empresa_ids', params.empresaIds.join(','));
      const qs = query.toString();
      return await apiClient.get<ApiResponse<CalendarioItemVirtual[]>>(`/projetot/calendario-eventos${qs ? `?${qs}` : ''}`);
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao buscar itens do ProjetoT para o calendário', data: [] };
    }
  },
};

export const projetotInvestimentoService = {
  async getAll(projetoId: string): Promise<ApiResponse<ProjetoInvestimento[]>> {
    try {
      return await apiClient.get<ApiResponse<ProjetoInvestimento[]>>(`/projetot/${projetoId}/investimentos`);
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao buscar investimentos', data: [] };
    }
  },

  async create(projetoId: string, data: Omit<ProjetoInvestimento, 'id' | 'projeto_id' | 'cronograma_id'>): Promise<ApiResponse<ProjetoInvestimento>> {
    try {
      return await apiClient.post<ApiResponse<ProjetoInvestimento>>(`/projetot/${projetoId}/investimentos`, data);
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao criar investimento', data: {} as ProjetoInvestimento };
    }
  },

  async update(projetoId: string, investimentoId: string, data: Partial<ProjetoInvestimento>): Promise<ApiResponse<ProjetoInvestimento>> {
    try {
      return await apiClient.put<ApiResponse<ProjetoInvestimento>>(`/projetot/${projetoId}/investimentos/${investimentoId}`, data);
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao atualizar investimento', data: {} as ProjetoInvestimento };
    }
  },

  async delete(projetoId: string, investimentoId: string): Promise<ApiResponse<boolean>> {
    try {
      return await apiClient.delete<ApiResponse<boolean>>(`/projetot/${projetoId}/investimentos/${investimentoId}`);
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao excluir investimento', data: false };
    }
  },
};
